import { validateEmail } from "@repo/shopify/utils/regex";
import React, { useEffect, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Button from "@atoms/Button";
import Input from "@atoms/Input";

import useFormErrorHandler from "@hooks/useFormErrorHandler";

interface IFormData {
  email: string;
  password?: string;
}

interface IProps {
  submit: ({
    email,
    password,
    acceptsMarketing,
  }: {
    email: string;
    password: string;
    acceptsMarketing?: boolean;
  }) => Promise<void>;

  createCustomerLoading: boolean;
}

export default function Register({ submit, createCustomerLoading }: IProps) {
  const { t } = useTranslation();

  const [isMarketing, setIsMarketing] = useState(false);

  const {
    register,
    handleSubmit,
    setFocus,
    getValues,
    clearErrors,
    formState: { errors },
  } = useForm<IFormData>({
    defaultValues: { email: "", password: "" },
    reValidateMode: "onSubmit",
  });

  useEffect(() => {
    setFocus("email");
  }, []);

  const { onCommonError, displayError } = useFormErrorHandler({ values: getValues(), errors });

  const onSubmit: SubmitHandler<IFormData> = async (formData) => {
    await submit({
      email: formData.email,
      password: formData.password || "",
      acceptsMarketing: isMarketing,
    });
  };

  return (
    <>
      <span className="text-title">Create Account</span>
      <form className="flex flex-col gap-2.5" onSubmit={handleSubmit(onSubmit, onCommonError)}>
        <Input
          {...displayError("email")}
          {...register("email", {
            required: { value: true, message: t("error.necessary_email") },
            validate: (value) => {
              if (!validateEmail(value)) return t("login.invalid_email");
            },
            onChange: () => {
              clearErrors("email");
            },
          })}
          label="Email"
          placeholder="Email Address"
          type="email"
        />
        <Input
          {...register("password", {
            required: { value: true, message: t("error.necessary_password") },
            minLength: { value: 5, message: t("login.min_password_title") },
            onChange: () => {
              clearErrors("password");
            },
          })}
          label="Password"
          placeholder="Password"
          type="password"
        />
        <Input
          checked={isMarketing}
          id="acceptMarketing"
          label={t("account.personal_information.accepts_marketing")}
          onChange={() => {
            setIsMarketing(!isMarketing);
          }}
          type="checkbox"
        />
        <div className="flex flex-col gap-2.5 pt-2.5">
          <Button as="square" className="w-full" loading={createCustomerLoading} name="login" type="submit">
            Create Account
          </Button>
        </div>
      </form>
    </>
  );
}
